import React, { useEffect, useState } from "react";
import { FaCheckCircle } from "react-icons/fa";
import { useCart } from "../../cart/context/CartContext";
import jsPDF from "jspdf";
import { trackOrder } from "../../orders/api";
import { logoBase64Invoice } from "../../../shared/utils/constants";
import { clearBuyNow, getBuyNow } from "../api";
import 'bootstrap/dist/css/bootstrap.min.css';

const ReturnSuccess = () => {
  const [orderId, setOrderId] = useState(null);
  const [loading, setLoading] = useState(false);
  const { clearCartAfterCheckout } = useCart();
  const [orderDetails, setOrderDetails] = useState(null);

  const redirectToHome = () => {
    sessionStorage.removeItem("orderId");
    sessionStorage.removeItem("onePayTransactionId");
    sessionStorage.removeItem("paymentResult");
    window.location.href = "/";
  };

  useEffect(() => {
    const storedOrderId = sessionStorage.getItem("orderId");
    if (storedOrderId) {
      setOrderId(storedOrderId);
      const buyNow = getBuyNow();
      if (buyNow) {
        clearBuyNow();
      } else {
        clearCartAfterCheckout();
      }

      trackOrder(storedOrderId)
        .then((data) => {
          setOrderDetails(data);
        })
        .catch((err) => {
          console.error("Error fetching order details:", err);
        });
    } else {
      redirectToHome();
    }
  }, []);

  const handleClick = () => {
    redirectToHome();
  };

  const handleDownloadInvoice = async () => {
    try {
      setLoading(true);
      const response = await trackOrder(orderId);
      setLoading(false);

      const doc = new jsPDF("p", "mm", "a4");
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let y = margin;

      const checkPageBreak = (neededSpace = 10) => {
        if (y + neededSpace > pageHeight - margin) {
          doc.addPage();
          y = margin;
        }
      };

      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      checkPageBreak(15);
      const logoWidth = 50;
      const logoHeight = 15;
      const logoX = pageWidth - margin - logoWidth;
      doc.addImage(logoBase64Invoice, "PNG", logoX, y, logoWidth, logoHeight);
      doc.text("Invoice", margin, y + logoHeight);
      y += logoHeight + 10;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      checkPageBreak(30);
      doc.text(`Order Number: ${response.orderNumber}`, margin, y);
      y += 6;
      doc.text(
        `Date: ${new Date(response.createdAt).toLocaleDateString()}`,
        margin,
        y
      );
      y += 6;
      doc.text(`Payment Method: ${response.paymentMethod}`, margin, y);
      y += 6;
      doc.text(`Status: ${response.status}`, margin, y);
      y += 10;

      const columnWidth = (pageWidth - 2 * margin) / 2;
      const billingX = margin;
      const shippingX = margin + columnWidth;
      let billY = y;
      let shipY = y;

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Billing Information", billingX, billY);
      doc.text("Shipping Information", shippingX, shipY);
      billY += 6;
      shipY += 6;

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Name: ${response.billingFirstName} ${response.billingLastName}`,
        billingX,
        billY
      );
      billY += 6;
      doc.text(`Email: ${response.billingEmail}`, billingX, billY);
      billY += 6;
      doc.text(`Phone: ${response.billingPhone}`, billingX, billY);
      billY += 6;
      const billingAddress = `Address: ${response.billingAddress1}, ${response.billingCity}, ${response.billingState}, ${response.billingPostcode}, ${response.billingCountry}`;
      doc.splitTextToSize(billingAddress, columnWidth).forEach((line) => {
        checkPageBreak(6);
        doc.text(line, billingX, billY);
        billY += 6;
      });

      doc.text(
        `Name: ${response.shippingFirstName} ${response.shippingLastName}`,
        shippingX,
        shipY
      );
      shipY += 6;
      doc.text(`Email: ${response.shippingEmail}`, shippingX, shipY);
      shipY += 6;
      doc.text(`Phone: ${response.shippingPhone}`, shippingX, shipY);
      shipY += 6;
      const shippingAddress = `Address: ${response.shippingAddress1}, ${response.shippingCity}, ${response.shippingState}, ${response.shippingPostcode}, ${response.shippingCountry}`;
      doc.splitTextToSize(shippingAddress, columnWidth).forEach((line) => {
        checkPageBreak(6);
        doc.text(line, shippingX, shipY);
        shipY += 6;
      });

      y = Math.max(billY, shipY) + 4;

      checkPageBreak(20);
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      const tableX = margin;
      let tableY = y;
      const tableWidth = pageWidth - 2 * margin;
      const col1Width = tableWidth * 0.6;
      const col2Width = tableWidth * 0.2;

      const headerHeight = 6;
      doc.text("Name", tableX + 2, tableY + 4);
      doc.text("Quantity", tableX + col1Width + 2, tableY + 4);
      doc.text("Price", tableX + col1Width + col2Width + 2, tableY + 4);
      doc.rect(tableX, tableY, tableWidth, headerHeight);
      doc.line(
        tableX + col1Width,
        tableY,
        tableX + col1Width,
        tableY + headerHeight
      );
      doc.line(
        tableX + col1Width + col2Width,
        tableY,
        tableX + col1Width + col2Width,
        tableY + headerHeight
      );
      tableY += headerHeight;

      doc.setFont("helvetica", "normal");
      response.products.forEach((product) => {
        const nameLines = doc.splitTextToSize(product.productName, col1Width - 4);
        const rowHeight = Math.max(nameLines.length * 6, 6) + 2;
        checkPageBreak(rowHeight);
        const rowY = tableY;

        doc.rect(tableX, rowY, tableWidth, rowHeight);
        doc.line(tableX + col1Width, rowY, tableX + col1Width, rowY + rowHeight);
        doc.line(
          tableX + col1Width + col2Width,
          rowY,
          tableX + col1Width + col2Width,
          rowY + rowHeight
        );

        let currentLineY = rowY + 4;
        nameLines.forEach((line) => {
          doc.text(line, tableX + 2, currentLineY);
          currentLineY += 6;
        });
        doc.text(product.quantity.toString(), tableX + col1Width + 2, rowY + 4);
        doc.text(
          "LKR " + product.price.toFixed(2),
          tableX + col1Width + col2Width + 2,
          rowY + 4
        );

        tableY += rowHeight;
      });
      y = tableY + 4;

      const summaryLabelX = pageWidth - margin - 50;
      const summaryPriceX = pageWidth - margin;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);

      doc.text("Subtotal:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.totalAmount.toFixed(2)}`, summaryPriceX, y, { align: "right" });
      y += 6;

      doc.text("Delivery Fee:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.deliveryFee.toFixed(2)}`, summaryPriceX, y, { align: "right" });
      y += 6;

      doc.text("Discount:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.orderDiscount.toFixed(2)}`, summaryPriceX, y, { align: "right" });
      y += 6;

      doc.text("Tax:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.orderTax.toFixed(2)}`, summaryPriceX, y, { align: "right" });
      y += 6;

      doc.setFont("helvetica", "bold");
      doc.text("Total:", summaryLabelX, y, { align: "right" });
      doc.text(`LKR ${response.netAmount.toFixed(2)}`, summaryPriceX, y, { align: "right" });

      y += 6;
      doc.setLineWidth(0.5);
      doc.line(margin, y, pageWidth - margin, y);
      y += 6;

      doc.setFont("helvetica", "normal");
      doc.text("https://bigmall.lk/", margin, y);
      y += 6;
      doc.text("Email: info@bigmall.lk", margin, y);
      y += 6;
      doc.text("Tel: (+94) 777961772 / (+94) 741337427", margin, y);

      doc.output("dataurlnewwindow");
    } catch (error) {
      console.error("Error generating invoice:", error);
      setLoading(false);
    }
  };

  return (
    <div className="checkout-sidebar">
      <div className="bg-color-three rounded-8 p-24 text-center">
        <h2 className="text-gray-900 text-xl fw-semibold">Payment Status</h2>
      </div>
      <div className="border border-gray-100 rounded-8 px-24 py-40 mt-24 text-center">
        <div className="mb-24">
          <FaCheckCircle size={60} color="green" />
        </div>
        <p className="text-gray-900 fw-medium text-lg mb-24">
          Your payment is successful!
        </p>
        {orderId && (
          <div className="mb-24">
            <p className="text-gray-700 text-sm">
              Order ID: <strong>{orderId}</strong>
            </p>
          </div>
        )}
        {orderDetails && (
          <div className="mb-24">
            <p className="text-gray-700 text-sm">
              Net Amount: <strong>LKR {orderDetails.netAmount?.toFixed(2)}</strong>
            </p>
          </div>
        )}
        <div className="mt-24">
          <button onClick={handleClick}>Click here to go back to Home</button>
        </div>
        <div className="mt-24">
          <button
            className={`btn btn-success px-10 py-7 ${loading ? "disabled opacity-75" : ""}`}
            onClick={handleDownloadInvoice}
            disabled={loading}
          >
            {loading ? "Generating Invoice..." : "View Invoice"}
          </button>
        </div>

        <div className="mt-32">
          <p className="text-gray-700 text-sm">Thank you for your order.</p>
        </div>
      </div>
    </div>
  );
};

export default ReturnSuccess;
